import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { toast } from 'sonner';
import { 
  RefreshCw, FileSpreadsheet, Download, BookOpen
} from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function JournalEntries() {
  const { api } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [exporting, setExporting] = useState(false);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchEntries();
  }, [selectedYear, selectedMonth, filterStatus]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedMonth && selectedMonth !== 'all') {
        params.append('month', selectedMonth);
      }
      if (filterStatus && filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      
      const res = await api.get(`/finance/journal-entries?${params.toString()}`);
      setEntries(res.data);
    } catch (error) {
      console.error('Failed to fetch journal entries:', error);
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let endpoint = `/finance/journal-entries/export?year=${selectedYear}`;
      let filename = `journal_entries_${selectedYear}`;
      
      if (selectedMonth && selectedMonth !== 'all') {
        endpoint += `&month=${selectedMonth}`;
        filename += `_${selectedMonth.toString().padStart(2, '0')}`;
      }
      filename += '.csv';
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Journal entries exported');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export journal entries');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-amber-100 text-amber-700', label: 'Draft' },
      reviewed: { color: 'bg-blue-100 text-blue-700', label: 'Reviewed' },
      approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
      exported: { color: 'bg-slate-100 text-slate-700', label: 'Exported' },
    };
    const c = config[status] || config.draft;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'revenue') {
      return <Badge className="bg-emerald-100 text-emerald-700">Revenue</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700">Expense</Badge>;
  };

  // Calculate totals
  const totalDebit = entries.reduce((sum, e) => {
    return sum + (e.entry_type === 'expense' ? e.amount : 0);
  }, 0);
  const totalCredit = entries.reduce((sum, e) => {
    return sum + (e.entry_type === 'revenue' ? e.amount : 0);
  }, 0);

  return (
    <div className="space-y-4" data-testid="journal-entries">
      {/* Summary Bar */}
      <div className="flex flex-wrap justify-between items-center gap-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex gap-4 text-sm">
          <span>Entries: <strong>{entries.length}</strong></span>
          <span className="text-red-600">Total Debits: <strong>${totalDebit.toLocaleString()}</strong></span>
          <span className="text-emerald-600">Total Credits: <strong>${totalCredit.toLocaleString()}</strong></span>
        </div>
      </div>

      {/* Filters and Export */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="exported">Exported</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={fetchEntries}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <Button onClick={handleExport} disabled={exporting || entries.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      {/* Journal Entries Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12">
          <FileSpreadsheet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No journal entries for this period</p>
          <p className="text-sm text-slate-400">Generate journal entries from the Ledger tab</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Ref</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Debit Account</TableHead>
                <TableHead>Credit Account</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} data-testid={`je-${entry.id}`}>
                  <TableCell className="text-slate-600 whitespace-nowrap">
                    {entry.entry_date}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {entry.transaction_reference}
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate">
                    {entry.description}
                  </TableCell>
                  <TableCell className="font-medium max-w-[120px] truncate">
                    {entry.vendor}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {entry.debit_account}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {entry.credit_account}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    ${entry.amount?.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {getTypeBadge(entry.entry_type)}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(entry.status)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Export Info */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          Journal Entry Export Format
        </h4>
        <p className="text-sm text-blue-700">
          The CSV export includes: Date, Reference, Description, Vendor, Debit Account, Credit Account, Amount, Entry Type, and Status. 
          This format is compatible with common accounting software and ready for your accountant.
        </p>
      </div>
    </div>
  );
}
